import React, { useRef, useState, useEffect } from "react";
import { Card, CardContent } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { Printer, PenTool, Sliders, ShoppingCart } from "lucide-react";
import { services, products, siteInfo } from "../../data/mock";

const iconMap = {
  Printer: Printer,
  PenTool: PenTool,
  Sliders: Sliders,
};

const useInView = (threshold = 0.1) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold }
    );
    const el = ref.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [threshold]);
  return [ref, isVisible];
};

/* ===== Services Section ===== */
export const ServicesSection = () => {
  const [ref, isVisible] = useInView(0.05);

  return (
    <section id="services" ref={ref} className="py-24 bg-white relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Heading */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-sm font-semibold text-sky-500 tracking-wider uppercase">
            What We Offer
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">
            Our Services
          </h2>
          <p className="text-slate-500 mt-4 max-w-2xl mx-auto">
            From design to delivery, we provide end-to-end 3D printing solutions
            tailored to your needs.
          </p>
        </div>

        {/* Service Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = iconMap[service.icon];
            return (
              <Card
                key={service.id}
                className={`group border border-sky-100/60 bg-white hover:shadow-xl hover:shadow-sky-100/40 hover:-translate-y-2 transition-all duration-500 cursor-default ${
                  isVisible
                    ? "opacity-100 translate-y-0"
                    : "opacity-0 translate-y-8"
                }`}
                style={{ transitionDelay: `${index * 150}ms` }}
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl bg-sky-50 group-hover:bg-sky-100 flex items-center justify-center mb-6 transition-colors duration-300">
                    <Icon className="w-7 h-7 text-sky-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-slate-500 leading-relaxed text-[15px]">
                    {service.description}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* ===== Product Catalog ===== */
export const ProductCatalog = () => {
  const [ref, isVisible] = useInView(0.05);

  const getProductLink = (productName) => {
    return `https://wa.me/${siteInfo.whatsappNumber}?text=${encodeURIComponent(
      `Hi Frostyware 3D! I'm interested in: ${productName}`
    )}`;
  };

  return (
    <section id="products" ref={ref} className="py-24 bg-slate-50/50 relative">
      {/* Subtle grid pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(14,165,233,0.015)_1px,transparent_1px),linear-gradient(90deg,rgba(14,165,233,0.015)_1px,transparent_1px)] bg-[size:40px_40px]" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        {/* Heading */}
        <div
          className={`text-center mb-16 transition-all duration-700 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <span className="text-sm font-semibold text-sky-500 tracking-wider uppercase">
            Our Products
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-3">
            Product Catalog
          </h2>
          <p className="text-slate-500 mt-4 max-w-2xl mx-auto">
            Browse our popular 3D printed products. Every item is crafted with
            precision and care.
          </p>
        </div>

        {/* Product Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product, index) => (
            <Card
              key={product.id}
              className={`group overflow-hidden border border-sky-100/50 bg-white hover:shadow-xl hover:shadow-sky-100/40 hover:-translate-y-1.5 transition-all duration-500 ${
                isVisible
                  ? "opacity-100 translate-y-0"
                  : "opacity-0 translate-y-8"
              }`}
              style={{ transitionDelay: `${index * 120}ms` }}
            >
              {/* Image */}
              <div className="relative overflow-hidden h-52">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/20 to-transparent" />
                <Badge className="absolute top-3 left-3 bg-sky-500/90 hover:bg-sky-500 text-white text-[11px] font-semibold backdrop-blur-sm border-0">
                  {product.tag}
                </Badge>
              </div>

              {/* Content */}
              <CardContent className="p-5">
                <h3 className="font-bold text-slate-900 text-lg leading-tight">
                  {product.name}
                </h3>
                <p className="text-xs text-sky-600 font-semibold mb-2 tracking-wide">
                  {product.subtitle}
                </p>
                <p className="text-sm text-slate-500 mb-4 line-clamp-2 leading-relaxed">
                  {product.description}
                </p>
                <div className="flex items-end justify-between">
                  <div>
                    {product.priceNote && (
                      <span className="text-[11px] text-slate-400 block">
                        {product.priceNote}
                      </span>
                    )}
                    <span
                      className={`font-bold ${
                        product.priceNote
                          ? "text-xl text-slate-900"
                          : "text-sm text-sky-600"
                      }`}
                    >
                      {product.price}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() =>
                      window.open(getProductLink(product.name), "_blank")
                    }
                    className="bg-sky-500 hover:bg-sky-600 text-white rounded-full text-xs h-9 px-4 shadow-sm shadow-sky-200/50 hover:shadow-md transition-all duration-300"
                  >
                    <ShoppingCart className="w-3.5 h-3.5 mr-1.5" />
                    Order
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};
